/**
 * Risk Assessment HUD - Floating AI Co-Pilot Widget
 * 
 * Features:
 * - Glassmorphism design with blur effect
 * - Minimized: Circular Heuristic Fit Score gauge with count-up animation
 * - Expanded: Full alert hierarchy (Compliance, Strategy, Drafting)
 * - Real-time updates on pursuit data changes
 * - Scanning status during document uploads
 * - AI Consultant Tips on demand
 */

import { useEffect, useState, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useRiskAssessment, ALERT_SEVERITY, ALERT_TYPES } from '../contexts/RiskAssessmentContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import {
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  AlertTriangle,
  AlertCircle,
  Check,
  XCircle,
  Crown,
  Calendar,
  Users,
  Award,
  FileText,
  Brain,
  Lightbulb,
  Sparkles,
  Loader2,
  Zap,
  Target,
  Shield,
  FileCheck,
  TrendingUp,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

// Icon mapping for alerts
const ALERT_ICONS = {
  ShieldAlert: ShieldAlert,
  AlertTriangle: AlertTriangle,
  AlertCircle: AlertCircle,
  Crown: Crown,
  Calendar: Calendar,
  Users: Users,
  Award: Award,
  FileText: FileText,
  Brain: Brain,
  Lightbulb: Lightbulb,
  Check: Check,
  XCircle: XCircle
};

// Severity color mapping
const SEVERITY_COLORS = {
  critical: {
    bg: 'bg-red-500/20',
    border: 'border-red-500/50',
    text: 'text-red-400',
    icon: 'text-red-400',
    glow: 'shadow-red-500/20'
  },
  warning: {
    bg: 'bg-amber-500/20',
    border: 'border-amber-500/50',
    text: 'text-amber-400',
    icon: 'text-amber-400',
    glow: 'shadow-amber-500/20'
  },
  success: {
    bg: 'bg-emerald-500/20',
    border: 'border-emerald-500/50',
    text: 'text-emerald-400',
    icon: 'text-emerald-400',
    glow: 'shadow-emerald-500/20'
  },
  info: {
    bg: 'bg-blue-500/20',
    border: 'border-blue-500/50',
    text: 'text-blue-400',
    icon: 'text-blue-400',
    glow: 'shadow-blue-500/20'
  }
};

// Animated counter hook
function useAnimatedCounter(targetValue, duration = 800) {
  const [displayValue, setDisplayValue] = useState(0);
  const startValueRef = useRef(0);
  const startTimeRef = useRef(null);
  const frameRef = useRef(null);
  
  useEffect(() => {
    startValueRef.current = displayValue;
    startTimeRef.current = null;
    
    const animate = (timestamp) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }
      
      const progress = Math.min((timestamp - startTimeRef.current) / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startValueRef.current + (targetValue - startValueRef.current) * eased);
      
      setDisplayValue(current);
      
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };
    
    frameRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [targetValue, duration]);
  
  return displayValue;
}

// Circular Gauge Component - Heuristic Fit Score
function HeuristicFitGauge({ score, size = 100, strokeWidth = 8 }) {
  const animatedScore = useAnimatedCounter(score);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (animatedScore / 100) * circumference;
  const offset = circumference - progress;
  
  // Color based on score
  const getScoreColor = (s) => {
    if (s >= 70) return '#10b981'; // Emerald
    if (s >= 50) return '#f59e0b'; // Amber
    return '#ef4444'; // Red
  };
  
  const color = getScoreColor(animatedScore);
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* Background circle */}
      <svg
        className="absolute inset-0 -rotate-90"
        width={size}
        height={size}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-300"
          style={{
            filter: `drop-shadow(0 0 6px ${color})`
          }}
        />
      </svg>
      
      {/* Score display */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span 
          className="text-2xl font-bold tabular-nums"
          style={{ color }}
        >
          {animatedScore}
        </span>
        <span className="text-[9px] text-slate-400 uppercase tracking-wider">Fit</span>
      </div>
    </div>
  );
}

// Alert Item Component
function AlertItem({ alert }) {
  const Icon = ALERT_ICONS[alert.icon] || AlertCircle;
  const colors = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.info;
  
  return (
    <div 
      className={`flex items-start gap-2 p-2 rounded-lg border ${colors.bg} ${colors.border} ${colors.glow}`}
      data-testid={`alert-${alert.id}`}
    >
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${colors.icon}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-medium ${colors.text}`}>{alert.title}</p>
        <p className="text-[10px] text-slate-400 truncate">{alert.message}</p>
      </div>
    </div>
  );
}

// Alert Category Section
function AlertCategory({ title, icon: Icon, alerts, defaultOpen = true }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  if (alerts.length === 0) return null;
  
  const criticalCount = alerts.filter(a => a.severity === ALERT_SEVERITY.CRITICAL).length;
  const warningCount = alerts.filter(a => a.severity === ALERT_SEVERITY.WARNING).length;
  const successCount = alerts.filter(a => a.severity === ALERT_SEVERITY.SUCCESS).length;
  
  return (
    <div className="space-y-1.5">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-xs text-slate-300 hover:text-white transition-colors"
      >
        <span className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" />
          <span className="font-medium">{title}</span>
          <span className="text-slate-500">({alerts.length})</span>
        </span>
        <div className="flex items-center gap-1">
          {criticalCount > 0 && (
            <Badge className="h-4 px-1 text-[9px] bg-red-500/30 text-red-400 border-red-500/50">
              {criticalCount}
            </Badge>
          )}
          {warningCount > 0 && (
            <Badge className="h-4 px-1 text-[9px] bg-amber-500/30 text-amber-400 border-amber-500/50">
              {warningCount}
            </Badge>
          )}
          {successCount > 0 && (
            <Badge className="h-4 px-1 text-[9px] bg-emerald-500/30 text-emerald-400 border-emerald-500/50">
              {successCount}
            </Badge>
          )}
          {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </div>
      </button>
      
      {isOpen && (
        <div className="space-y-1.5 pl-1">
          {alerts.map(alert => (
            <AlertItem key={alert.id} alert={alert} />
          ))}
        </div>
      )}
    </div>
  );
}

// Scanning Status Indicator
function ScanningIndicator({ status }) {
  const activeScans = Object.entries(status).filter(([_, s]) => s === 'scanning');
  
  if (activeScans.length === 0) return null;
  
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/50">
      <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin" />
      <span className="text-[10px] text-blue-300">
        Scanning {activeScans.length} document{activeScans.length > 1 ? 's' : ''}...
      </span>
    </div>
  );
}

// Main HUD Component
export default function RiskAssessmentHUD() {
  const {
    currentPursuit,
    isExpanded,
    isVisible,
    heuristicFitScore,
    categorizedAlerts,
    scanningStatus,
    consultantTip,
    loadingTip,
    toggleExpanded,
    fetchConsultantTip
  } = useRiskAssessment();
  const { pursuitId: urlPursuitId } = useParams();

  // Memoized factor breakdown — Object.entries + filter + map ran on every
  // render before. Now only recomputes when the breakdown object identity
  // changes (i.e. after a fresh AI analysis run).
  // Hook MUST be called before any early returns to satisfy React's
  // rules-of-hooks (consistent call order across renders).
  const fitFactorRows = useMemo(() => {
    const breakdown = currentPursuit?.fit_score_breakdown?.details;
    if (!breakdown) return [];
    return Object.entries(breakdown)
      .filter(([key, val]) => key.startsWith('factor_') && val?.score !== undefined && val?.max !== undefined)
      .map(([key, factor]) => {
        const pct = Math.round((factor.score / factor.max) * 100);
        return { key, factor, pct, isGap: pct < 40 };
      });
  }, [currentPursuit?.fit_score_breakdown?.details]);

  // Don't render unless:
  //   1. Visibility flag is on (set by DashboardLayout for pursuit routes)
  //   2. The URL contains a specific pursuit id (we are inside ONE pursuit)
  //   3. The in-memory `currentPursuit` actually matches that URL id
  // This is the last line of defense against tenant data bleeding between
  // routes/sessions: if the React state still has another pursuit's data
  // (because the new page hasn't loaded yet), we refuse to paint stale info.
  if (!isVisible) return null;
  if (!urlPursuitId) return null;
  if (!currentPursuit?.id || currentPursuit.id !== urlPursuitId) return null;
  
  // Count alerts by severity for quick view
  const criticalCount = categorizedAlerts.compliance.filter(a => a.severity === ALERT_SEVERITY.CRITICAL).length;
  const warningCount = [
    ...categorizedAlerts.compliance,
    ...categorizedAlerts.strategy
  ].filter(a => a.severity === ALERT_SEVERITY.WARNING).length;
  const successCount = [
    ...categorizedAlerts.strategy,
    ...categorizedAlerts.drafting
  ].filter(a => a.severity === ALERT_SEVERITY.SUCCESS).length;
  
  return (
    <div 
      className={`fixed right-4 top-1/2 -translate-y-1/2 z-50 transition-all duration-300 ease-out ${
        isExpanded ? 'w-72' : 'w-auto'
      }`}
      data-testid="risk-assessment-hud"
    >
      {/* Glassmorphism Container */}
      <div 
        className={`
          relative overflow-hidden rounded-2xl
          bg-slate-900/80 backdrop-blur-xl
          border border-white/10
          shadow-2xl shadow-black/50
          transition-all duration-300
          ${isExpanded ? 'p-4' : 'p-2'}
        `}
      >
        {/* Gradient Accent */}
        <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 via-transparent to-purple-500/10 pointer-events-none" />
        
        {/* Content */}
        <div className="relative">
          {/* Header with Toggle */}
          <div className="flex items-center justify-between mb-3">
            {isExpanded && (
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-teal-400" />
                <span className="text-xs font-semibold text-white">Risk Assessment</span>
              </div>
            )}
            
            <button
              onClick={toggleExpanded}
              className={`
                p-1.5 rounded-lg
                bg-white/5 hover:bg-white/10
                text-slate-400 hover:text-white
                transition-all duration-200
                ${!isExpanded ? 'mx-auto' : ''}
              `}
              data-testid="hud-toggle-btn"
            >
              {isExpanded ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>
          
          {/* Main Content */}
          {isExpanded ? (
            <div className="space-y-4">
              {/* Score Gauge or State Message */}
              {(() => {
                // Determine score state
                const hasAIAnalysis = currentPursuit?.ai_analysis_completed === true;
                const rfpCount = currentPursuit?.rfp_document_count || 0;
                const capCount = currentPursuit?.capability_document_count || 0;
                const hasMetadata = !!(
                  (currentPursuit?.agency_id || currentPursuit?.agency_name || currentPursuit?.agency_code || currentPursuit?.contracting_agency) &&
                  (currentPursuit?.naics_code || currentPursuit?.primary_naics) &&
                  (currentPursuit?.opportunity_name || currentPursuit?.title || currentPursuit?.description)
                );
                const hasDocs = rfpCount > 0 && capCount > 0;
                const prereqsMet = hasMetadata && hasDocs;

                if (hasAIAnalysis && heuristicFitScore !== null) {
                  // AI Analyzed — show gauge + breakdown
                  const breakdown = currentPursuit?.fit_score_breakdown?.details || null;
                  return (
                    <div className="space-y-3">
                      <div className="flex justify-center">
                        <HeuristicFitGauge score={heuristicFitScore} size={90} strokeWidth={6} />
                      </div>
                      {breakdown && (
                        <div className="space-y-1.5 px-1">
                          {fitFactorRows.map(({ key, factor, pct, isGap }) => (
                              <div key={key} className="group" data-testid={`fit-factor-${key}`}>
                                <div className="flex items-center justify-between text-[10px]">
                                  <span className={isGap ? "text-amber-400" : "text-slate-300"}>{factor.name}</span>
                                  <span className={`font-mono font-medium ${isGap ? "text-amber-400" : "text-emerald-400"}`}>{factor.score}/{factor.max}</span>
                                </div>
                                <div className="h-1 bg-white/10 rounded-full mt-0.5 overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full transition-all ${isGap ? "bg-amber-500/70" : "bg-emerald-500/70"}`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  );
                }

                if (prereqsMet) {
                  // Analysis Required
                  return (
                    <div className="text-center space-y-2 py-2">
                      <div className="w-14 h-14 mx-auto rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                        <Target className="w-6 h-6 text-amber-400" />
                      </div>
                      <p className="text-xs text-amber-300 font-medium">Analysis Required</p>
                      <p className="text-[10px] text-slate-400 leading-relaxed px-2">
                        Run AI Analysis on the Ingestion Hub to calculate the Fit Score.
                      </p>
                    </div>
                  );
                }

                // Prerequisites Incomplete
                return (
                  <div className="text-center space-y-2 py-2">
                    <div className="w-14 h-14 mx-auto rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
                      <ShieldAlert className="w-6 h-6 text-red-400" />
                    </div>
                    <p className="text-xs text-red-300 font-medium">Prerequisites Incomplete</p>
                    <div className="text-[10px] text-slate-400 leading-relaxed px-2 space-y-1">
                      {!hasMetadata && <p className="text-red-400">Complete metadata (Agency, NAICS, Description)</p>}
                      {rfpCount === 0 && <p className="text-red-400">Upload RFP/Solicitation document</p>}
                      {capCount === 0 && <p className="text-red-400">Upload Capability document</p>}
                    </div>
                  </div>
                );
              })()}
              
              {/* Quick Stats */}
              <div className="flex items-center justify-center gap-3 text-[10px]">
                {criticalCount > 0 && (
                  <span className="flex items-center gap-1 text-red-400">
                    <XCircle className="w-3 h-3" />
                    {criticalCount} Critical
                  </span>
                )}
                {warningCount > 0 && (
                  <span className="flex items-center gap-1 text-amber-400">
                    <AlertTriangle className="w-3 h-3" />
                    {warningCount} Warnings
                  </span>
                )}
                {successCount > 0 && (
                  <span className="flex items-center gap-1 text-emerald-400">
                    <Check className="w-3 h-3" />
                    {successCount} Wins
                  </span>
                )}
              </div>
              
              {/* Scanning Status */}
              <ScanningIndicator status={scanningStatus} />
              
              {/* Alert Categories */}
              <div className="space-y-3 max-h-[280px] overflow-y-auto custom-scrollbar pr-1">
                <AlertCategory
                  title="Compliance"
                  icon={Shield}
                  alerts={categorizedAlerts.compliance}
                  defaultOpen={true}
                />
                <AlertCategory
                  title="Strategy"
                  icon={TrendingUp}
                  alerts={categorizedAlerts.strategy}
                  defaultOpen={categorizedAlerts.compliance.length === 0}
                />
                <AlertCategory
                  title="Drafting"
                  icon={FileCheck}
                  alerts={categorizedAlerts.drafting}
                  defaultOpen={false}
                />
              </div>
              
              {/* AI Consultant Tip */}
              {(consultantTip || loadingTip) && (
                <div className="mt-3 p-2.5 rounded-lg bg-purple-500/20 border border-purple-500/50">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                    <span className="text-[10px] font-medium text-purple-300">AI Consultant Tip</span>
                  </div>
                  {loadingTip ? (
                    <div className="flex items-center gap-2 text-[10px] text-purple-300">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Analyzing strategy...
                    </div>
                  ) : (
                    <p className="text-[10px] text-slate-300 leading-relaxed">{consultantTip}</p>
                  )}
                </div>
              )}
              
              {/* Get AI Tip Button */}
              {!consultantTip && !loadingTip && currentPursuit && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={fetchConsultantTip}
                  className="w-full h-7 text-[10px] text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                  data-testid="get-ai-tip-btn"
                >
                  <Sparkles className="w-3 h-3 mr-1.5" />
                  Get AI Strategy Tip
                </Button>
              )}
              
              {/* Pursuit Name */}
              {currentPursuit && (
                <div className="pt-2 border-t border-white/10">
                  <p className="text-[9px] text-slate-500 truncate">
                    {currentPursuit.opportunity_name}
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* Minimized View */
            <div className="flex flex-col items-center gap-2">
              {/* Mini Gauge or State Icon */}
              {(() => {
                const hasAIAnalysis = currentPursuit?.ai_analysis_completed === true;
                if (hasAIAnalysis && heuristicFitScore !== null) {
                  return <HeuristicFitGauge score={heuristicFitScore} size={56} strokeWidth={4} />;
                }
                const rfpCount = currentPursuit?.rfp_document_count || 0;
                const capCount = currentPursuit?.capability_document_count || 0;
                const hasMetadata = !!(
                  (currentPursuit?.agency_id || currentPursuit?.agency_name || currentPursuit?.agency_code || currentPursuit?.contracting_agency) &&
                  (currentPursuit?.naics_code || currentPursuit?.primary_naics) &&
                  (currentPursuit?.opportunity_name || currentPursuit?.title || currentPursuit?.description)
                );
                const prereqsMet = hasMetadata && rfpCount > 0 && capCount > 0;
                if (prereqsMet) {
                  return (
                    <div className="w-14 h-14 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center" title="Run AI Analysis to calculate Fit Score">
                      <Target className="w-5 h-5 text-amber-400" />
                    </div>
                  );
                }
                return (
                  <div className="w-14 h-14 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center" title="Prerequisites incomplete">
                    <ShieldAlert className="w-5 h-5 text-red-400" />
                  </div>
                );
              })()}
              
              {/* Alert Indicators */}
              <div className="flex flex-col gap-1">
                {criticalCount > 0 && (
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" title={`${criticalCount} critical`} />
                )}
                {warningCount > 0 && (
                  <div className="w-2 h-2 rounded-full bg-amber-500" title={`${warningCount} warnings`} />
                )}
                {successCount > 0 && (
                  <div className="w-2 h-2 rounded-full bg-emerald-500" title={`${successCount} wins`} />
                )}
              </div>
              
              {/* Scanning indicator */}
              {Object.values(scanningStatus).some(s => s === 'scanning') && (
                <Loader2 className="w-3 h-3 text-blue-400 animate-spin" />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
